/**
 * InlineRenameField: Finder-style rename in place. Every desktop view (icon
 * tiles, cards, List rows) and the sidebar tree draw it in their name slot
 * while `useIsRenaming(item.id, surface)` holds; F2, the context menu's and
 * the action bar's Rename start it (`selectionStore.startRename`).
 *
 * It starts focused with the base name selected (`baseNameEnd`, so a file
 * keeps ".xyz"). Enter commits through the shared `renameItem`; Escape and
 * a click away cancel (a click away is "never mind", David 2026-10-02); an
 * empty or unchanged name cancels. A refusal (a name clash, the collection
 * name limits) shows under the field and editing goes on. Enter and Escape
 * hand focus back to the item; a click away leaves it where the user put it. A save that finishes after the field has closed (the user
 * started another rename, or moved to another folder) leaves the current
 * rename and focus alone, and reports a refusal in a toast.
 *
 * The field belongs to an item that is a grid cell, row or tree item with
 * its own keys, clicks and drag, so it keeps all of them to itself: keydown,
 * pointer and click events stop here, and the item's drag props
 * (`useItemDrag`) make it not draggable while it is being renamed, so a drag
 * in the text selects text instead of moving the item.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { Box, Input } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import type { WorkspaceItem } from '@bower/shared';

import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { useWorkspaceId } from '../../../contexts/WorkspaceContext';
import { useAppToast } from '../../../hooks/ui/useAppToast';
import { useSelectionStore, type ItemSurface } from '../../../stores/selectionStore';
import { safeAnalytics } from '../../../utils/analytics';
import { baseNameEnd, renameItem } from './renameItem';
import { refreshFolderLists } from '../../../lib/folderListRefresh';

/** The focusable items a field can sit in, which get focus back. */
const ITEM_HOSTS = '[role="gridcell"], [role="row"], [role="treeitem"]';

/** Is this item being renamed in place on this surface? */
export function useIsRenaming(id: string, surface: ItemSurface): boolean {
  return useSelectionStore((s) => s.renaming?.id === id && s.renaming.surface === surface);
}

interface InlineRenameFieldProps {
  readonly item: WorkspaceItem;
  readonly surface: ItemSurface;
  /** Match the name it replaces. */
  readonly fontSize?: string;
  readonly textAlign?: 'left' | 'center';
}

export function InlineRenameField({
  item,
  surface,
  fontSize = 'sm',
  textAlign = 'left',
}: InlineRenameFieldProps) {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  const toast = useAppToast();
  const errorId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const hostRef = useRef<HTMLElement | null>(null);
  const [name, setName] = useState(item.name);
  const [selectEnd] = useState(() => baseNameEnd(item));
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  /** Saving or closing: the blur that disabling or closing causes is not a cancel. */
  const busy = useRef(false);
  /** A refused save takes focus back once the field is enabled again. */
  const refocus = useRef(false);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    hostRef.current = input.closest<HTMLElement>(ITEM_HOSTS);
    input.focus({ preventScroll: true });
    input.setSelectionRange(0, selectEnd);
  }, [selectEnd]);

  useEffect(() => {
    if (isSaving || !refocus.current) return;
    refocus.current = false;
    inputRef.current?.focus({ preventScroll: true });
  }, [isSaving]);

  /** Still the rename on screen (not replaced by another, not unmounted by a scope change)? */
  const isActive = () => {
    const { renaming } = useSelectionStore.getState();
    return renaming?.id === item.id && renaming.surface === surface;
  };

  const close = (returnFocus: boolean) => {
    busy.current = true;
    if (returnFocus && isActive()) hostRef.current?.focus({ preventScroll: true });
    useSelectionStore.getState().stopRename(item.id);
  };

  const commit = async () => {
    if (busy.current) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === item.name) return close(true);
    busy.current = true;
    setError(null);
    setIsSaving(true);
    const result = await renameItem(item, trimmed);
    if (!result.ok) {
      const message = result.error ?? 'Could not rename.';
      if (!isActive()) {
        toast({ title: `Could not rename "${item.name}"`, description: message, status: 'error' });
        return;
      }
      busy.current = false;
      refocus.current = true;
      setError(message);
      setIsSaving(false);
      return;
    }
    safeAnalytics.track(ANALYTICS_EVENTS.WORKSPACE_ITEM_RENAMED, { surface, kind: item.kind });
    refreshFolderLists(queryClient, { queryKey: ['workspace-items', { workspaceId }] });
    void queryClient.invalidateQueries({ queryKey: ['hierarchy'] });
    close(true);
  };

  const stop = (event: { stopPropagation: () => void }) => event.stopPropagation();

  return (
    <Box
      position="relative"
      flex={1}
      minW={0}
      w="full"
      onKeyDown={stop}
      onPointerDown={stop}
      onMouseDown={stop}
      onClick={stop}
      onDoubleClick={stop}
      onContextMenu={stop}
    >
      <Input
        ref={inputRef}
        aria-label="Name"
        isInvalid={error !== null}
        aria-describedby={error ? errorId : undefined}
        value={name}
        isDisabled={isSaving}
        size="xs"
        fontSize={fontSize}
        textAlign={textAlign}
        borderRadius="sm"
        bg="bg.surface"
        userSelect="text"
        onChange={(event) => setName(event.target.value)}
        onBlur={() => {
          if (!busy.current) close(false);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void commit();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            close(true);
          }
        }}
      />
      {error && (
        <Box
          id={errorId}
          role="alert"
          position="absolute"
          top="100%"
          left={0}
          mt={1}
          minW="200px"
          maxW="320px"
          px={2}
          py={1}
          zIndex="dropdown"
          bg="bg.surface"
          border="1px solid"
          borderColor="border.error"
          borderRadius="sm"
          boxShadow="sm"
          fontSize="xs"
          textAlign="left"
          color="text.error"
          whiteSpace="normal"
        >
          {error}
        </Box>
      )}
    </Box>
  );
}
